import React, { useEffect, useState } from 'react';
import { Box, Container, Tooltip, IconButton, useTheme } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import ScrollToTop from '../components/ScrollToTop';
import BackgroundAtmosphere from '../components/primitives/BackgroundAtmosphere';
import ReadingProgressBar from '../components/reading/ReadingProgressBar';
import UnixShellModal from '../components/terminal/UnixShellModal';
import CommandPalette from '../components/palette/CommandPalette';
import RecruiterModal from '../components/recruiter/RecruiterModal';
import { useSpotlight } from '../hooks/useSpotlight';
import { playClickSound } from '../utils/soundEffects';
import { updatePageSEO } from '../utils/seo';

const MainLayout = ({ children, mode, toggleTheme }) => {
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recruiterOpen, setRecruiterOpen] = useState(false);

  // Initialize hardware-accelerated dynamic cursor spotlight
  useSpotlight();

  useEffect(() => {
    document.body.className = mode === 'dark' ? 'theme-dark' : 'theme-light';
  }, [mode]);

  useEffect(() => {
    updatePageSEO(location.pathname);
  }, [location.pathname]);

  // Global Keyboard Shortcuts (Cmd+K / Ctrl+K and /)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
        return;
      }

      // '/' to search when not typing in an input
      if (
        e.key === '/' &&
        !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) &&
        !document.activeElement?.isContentEditable
      ) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    const handleCustomRecruiter = () => handleOpenRecruiter();
    window.addEventListener('open-recruiter-modal', handleCustomRecruiter);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('open-recruiter-modal', handleCustomRecruiter);
    };
  }, []);

  const handleOpenTerminal = () => {
    playClickSound();
    setTerminalOpen(true);
  };

  const handleOpenPalette = () => {
    playClickSound();
    setPaletteOpen(true);
  };

  const handleOpenRecruiter = () => {
    playClickSound();
    setRecruiterOpen(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'transparent',
        color: 'text.primary',
      }}
    >
      <ReadingProgressBar />
      <BackgroundAtmosphere />
      <ScrollToTop />

      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Navbar
          mode={mode}
          toggleTheme={toggleTheme}
          onOpenTerminal={handleOpenTerminal}
          onOpenPalette={handleOpenPalette}
          onOpenRecruiter={handleOpenRecruiter}
        />
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: { xs: 3, md: 5 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '100%', md: '1440px', lg: '1620px', xl: '1760px' },
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Container>
      </Box>

      {/* Floating Unix Terminal Launcher (Compact Icon FAB) */}
      <Box
        className="no-print"
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 28 },
          right: { xs: 20, sm: 28 },
          zIndex: 1200,
        }}
      >
        <Tooltip title="Launch Unix Web Shell ( >_ )" arrow placement="left">
          <IconButton
            onClick={handleOpenTerminal}
            aria-label="Open Unix Terminal"
            sx={{
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              borderRadius: '50%',
              backgroundColor: theme.palette.secondary.main,
              color: '#000000',
              boxShadow: isDark 
                ? '0 8px 24px rgba(0, 0, 0, 0.6), 0 0 20px rgba(229, 169, 60, 0.4)'
                : '0 8px 20px rgba(0, 0, 0, 0.15), 0 0 16px rgba(184, 134, 11, 0.3)',
              border: `1px solid ${isDark ? 'rgba(229, 169, 60, 0.5)' : 'rgba(184, 134, 11, 0.5)'}`,
              backdropFilter: 'blur(12px)',
              transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              '&:hover': {
                backgroundColor: theme.palette.secondary.light || '#f5be58',
                color: '#000000',
                transform: 'translateY(-3px) scale(1.08)',
                boxShadow: isDark
                  ? '0 12px 32px rgba(0, 0, 0, 0.75), 0 0 28px rgba(229, 169, 60, 0.6)'
                  : '0 12px 28px rgba(0, 0, 0, 0.22), 0 0 22px rgba(184, 134, 11, 0.45)',
              },
            }}
          >
            <TerminalIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Global Command Palette Modal (Cmd+K / Ctrl+K) */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        mode={mode}
        toggleTheme={toggleTheme}
        onOpenTerminal={handleOpenTerminal}
        onOpenRecruiter={handleOpenRecruiter}
      />

      {/* 30-Second Recruiter & Hiring Manager Executive Briefing Modal */}
      <RecruiterModal
        open={recruiterOpen}
        onClose={() => setRecruiterOpen(false)}
      />

      {/* Interactive Unix Web Shell Modal */}
      <UnixShellModal
        open={terminalOpen}
        onClose={() => setTerminalOpen(false)}
        toggleTheme={toggleTheme}
        mode={mode}
      />

      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default MainLayout;
